import supabase from '../supabase/supabase';

const GUEST_CUSTOM_FOODS_KEY = 'meal_calculator_guest_foods';

// Helper to format food from DB to app format
const formatFromDb = (dbFood) => ({
  id: dbFood.id,
  name: dbFood.name,
  category: dbFood.category,
  calories: dbFood.calories,
  protein: dbFood.protein,
  carbs: dbFood.carbs,
  fat: dbFood.fat,
  servingSize: dbFood.serving_size,
  servingUnit: dbFood.serving_unit,
  servingOptions: dbFood.serving_options,
  isCustom: true,
});

// Helper to format food from app to DB format
const formatToDb = (appFood) => ({
  name: appFood.name,
  category: appFood.category,
  calories: appFood.calories,
  protein: appFood.protein,
  carbs: appFood.carbs,
  fat: appFood.fat,
  serving_size: appFood.servingSize,
  serving_unit: appFood.servingUnit,
  serving_options: appFood.servingOptions,
});

class CustomFoodService {
  _getGuestFoods() {
    try {
      return JSON.parse(localStorage.getItem(GUEST_CUSTOM_FOODS_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  _setGuestFoods(foods) {
    localStorage.setItem(GUEST_CUSTOM_FOODS_KEY, JSON.stringify(foods));
  }
  
  async getCustomFoods() {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return this._getGuestFoods();
    }

    const { data, error } = await supabase
      .from('custom_foods')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching custom foods:', error);
      return [];
    }
    return data.map(formatFromDb);
  }

  async addCustomFood(foodData) {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      const guestFoods = this._getGuestFoods();
      const newFood = {
        ...foodData,
        id: `guest_${Date.now()}`,
        isCustom: true,
      };
      guestFoods.unshift(newFood);
      this._setGuestFoods(guestFoods);
      return newFood;
    }

    const foodToInsert = {
      ...formatToDb(foodData),
      user_id: user.id,
    };

    const { data, error } = await supabase
      .from('custom_foods')
      .insert(foodToInsert)
      .select()
      .single();

    if (error) {
      console.error('Error adding custom food:', error);
      return null;
    }
    return formatFromDb(data);
  }

  async updateCustomFood(foodId, foodData) {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      const guestFoods = this._getGuestFoods();
      const updatedFoods = guestFoods.map(food =>
        food.id === foodId ? { ...food, ...foodData } : food
      );
      this._setGuestFoods(updatedFoods);
      return updatedFoods.find(f => f.id === foodId);
    }

    const foodToUpdate = formatToDb(foodData);
    const { data, error } = await supabase
      .from('custom_foods')
      .update(foodToUpdate)
      .eq('id', foodId)
      .select()
      .single();

    if (error) {
      console.error('Error updating custom food:', error);
      return null;
    }
    return formatFromDb(data);
  }

  async deleteCustomFood(foodId) {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      const guestFoods = this._getGuestFoods();
      const updatedFoods = guestFoods.filter(food => food.id !== foodId);
      this._setGuestFoods(updatedFoods);
      return true;
    }

    const { error } = await supabase
      .from('custom_foods')
      .delete()
      .eq('id', foodId);

    if (error) {
      console.error('Error deleting custom food:', error);
      return false;
    }
    return true;
  }
  
  async carryOverGuestFoods(userId) {
    const guestFoods = this._getGuestFoods();
    if (!userId || guestFoods.length === 0) {
      return;
    }

    console.log(`Carrying over ${guestFoods.length} guest foods to user ${userId}`);

    const foodsToInsert = guestFoods.map(food => ({
      ...formatToDb(food),
      user_id: userId,
    }));

    const { error } = await supabase.from('custom_foods').insert(foodsToInsert);

    if (error) {
      console.error('Error carrying over guest foods:', error);
      // Don't clear local storage if the DB operation failed
      return;
    }
    
    // Clear guest foods from local storage on success
    localStorage.removeItem(GUEST_CUSTOM_FOODS_KEY);
    console.log('Guest custom foods carried over and cleared from local storage.');
  }
}

export default new CustomFoodService();